import {
  GaPassCategory,
  type CityLocation,
  type EventListGridItem,
  type GaPassClassification,
  type SiteVenueLocation,
} from "src/http/api-types";
import type { PassTypeFilter } from "./ga-pass-type-chips";

/**
 * Returns true if the classification represents an unclassified event
 * (category is None or missing). These events sort to the end of the list
 * and only apply filters whose metadata is actually present (e.g. skip
 * weekend filter when grouping is null, skip dayCount filter when dayCount
 * is null).
 */
export const isUnclassifiedEvent = (classification: GaPassClassification | undefined): boolean =>
  !classification || classification.category === GaPassCategory.None;

/** Shared empty grid-item list — a stable reference keeps consumers' useMemo deps from churning. */
export const EMPTY_GRID_ITEMS: readonly EventListGridItem[] = [];

/** Maps a PassTypeFilter to the set of GaPassCategory values it matches. */
const PASS_TYPE_CATEGORIES: Record<PassTypeFilter, ReadonlySet<GaPassCategory | undefined>> = {
  festival: new Set([GaPassCategory.Ticket, GaPassCategory.None, undefined]),
  camping: new Set([GaPassCategory.Camping]),
  parking: new Set([GaPassCategory.Parking]),
  shuttle: new Set([GaPassCategory.Shuttle]),
};

/**
 * Returns true if the given category matches the active pass-type filter.
 * When filter is null (no filter active), all categories match.
 */
export const matchesPassTypeFilter = (filter: PassTypeFilter | null, category: GaPassCategory | undefined): boolean => {
  if (filter === null) return true;
  return PASS_TYPE_CATEGORIES[filter].has(category);
};

/**
 * Returns true if the event's weekend grouping matches the active weekend filter.
 * When weekend is null (no filter active), all groupings match.
 * Events without a grouping always match (not weekend-specific).
 */
export const matchesWeekendFilter = (weekend: string | null, grouping: string | null | undefined): boolean => {
  if (weekend === null) return true;
  if (!grouping) return true;
  return grouping === weekend;
};

/**
 * Returns true if the event's venue city matches the active city filter.
 * When city is null (no filter active), all cities match.
 * Events with no venueCity are hidden when a city filter is active.
 * Comparison is case-insensitive to guard against casing mismatches
 * between citySuggestions and venueCity data sources.
 */
export const matchesCityFilter = (city: string | null, venueCity: string | null | undefined): boolean => {
  if (city === null) return true;
  return !!venueCity && venueCity.toLowerCase() === city.toLowerCase();
};

/**
 * Returns true if the event's day count matches the active filter.
 * When filter is null (no filter active), all pass lengths match.
 * Pass classification.dayCount (computed by the backend from tier text or event dates).
 */
export const matchesDayCountFilter = (
  filterDayCount: number | null | undefined,
  effectiveDayCount: number | null,
): boolean => {
  if (filterDayCount === null || filterDayCount === undefined) return true;
  if (effectiveDayCount === null) return false;
  return effectiveDayCount === filterDayCount;
};

/**
 * Find the citySuggestion closest to the given coordinates (squared distance).
 * Returns the suggestion's city name, or the fallback name if no match is found.
 * Used to map a geocoder result back to a backend-known city name.
 */
export const findClosestCitySuggestion = (
  citySuggestions: readonly SiteVenueLocation[] | null | undefined,
  lat: number,
  lng: number,
  fallback: string,
): string => {
  if (!citySuggestions || citySuggestions.length === 0) return fallback;
  let bestCity = fallback;
  let bestDist = Infinity;
  for (const s of citySuggestions) {
    if (!s.city || s.lat === null || s.lat === undefined || s.long === null || s.long === undefined) {
      continue;
    }

    const d = (s.lat - lat) ** 2 + (s.long - lng) ** 2;
    if (d < bestDist) {
      bestDist = d;
      bestCity = s.city;
    }
  }
  return bestCity;
};

/** Result of the single-pass grid derivation. */
export interface GridDerivedData {
  /** Pass-type chips that have events at the active location. */
  filters: Set<PassTypeFilter>;
  /** Weekend grouping keys for the active pass type + city. */
  weekendKeys: Set<string>;
  /** Day-count values for the active pass type + city. */
  dayCounts: Set<number>;
  /** Number of events matching the active pass type + city. */
  eventCount: number;
  /** First event's date range per weekend grouping. */
  weekendDateRanges: Map<string, string>;
}

/**
 * Single-pass derivation over grid items. Computes all filter-related
 * data in one loop: available pass-type chips, weekend keys with date
 * ranges, day counts, and filtered event count.
 *
 * Extracted as a pure function for testability — the context wraps
 * this in useMemo with the appropriate dependencies.
 */
export const deriveGridFilterData = (
  gridItems: readonly EventListGridItem[],
  classifications: Readonly<Record<string, GaPassClassification>> | undefined,
  effectiveCity: string | null,
  passTypeFilter: PassTypeFilter | null,
): GridDerivedData => {
  const filters = new Set<PassTypeFilter>();
  const weekendKeys = new Set<string>();
  const dayCounts = new Set<number>();
  let eventCount = 0;
  const weekendDateRanges = new Map<string, string>();

  if (!classifications) {
    return { filters, weekendKeys, dayCounts, eventCount, weekendDateRanges };
  }

  for (const item of gridItems) {
    if (!matchesCityFilter(effectiveCity, item.venueCity)) continue;
    const c = classifications[String(item.eventId)];

    if (isUnclassifiedEvent(c)) {
      if (matchesPassTypeFilter(passTypeFilter, c?.category)) {
        eventCount++;
        // Contribute available metadata to filter options —
        // None-category events may still carry valid grouping or dayCount.
        if (c?.grouping && !weekendKeys.has(c.grouping)) {
          weekendKeys.add(c.grouping);
          const dateRange = item.formattedDate
            ? item.formattedDateYear
              ? `${item.formattedDate}, ${item.formattedDateYear}`
              : item.formattedDate
            : null;
          if (dateRange) weekendDateRanges.set(c.grouping, dateRange);
        }
        if (c?.dayCount !== null && c?.dayCount !== undefined && c.dayCount > 0) {
          dayCounts.add(c.dayCount);
        }
      }
      continue;
    }
    // Logically unreachable — isUnclassifiedEvent(c) above already catches
    // undefined and continues. But isUnclassifiedEvent returns boolean, not a
    // type predicate, so TS can't narrow c from GaPassClassification | undefined
    // to GaPassClassification in the code below. Without this guard every
    // c.category / c.grouping / c.dayCount reference would fail with TS18048.
    if (!c) continue;

    // Available pass-type chips
    if (c.category === GaPassCategory.Camping) filters.add("camping");
    if (c.category === GaPassCategory.Parking) filters.add("parking");
    if (c.category === GaPassCategory.Shuttle) filters.add("shuttle");

    // Everything below is scoped to the active pass type
    if (!matchesPassTypeFilter(passTypeFilter, c.category)) continue;

    eventCount++;

    // Weekend keys
    if (c.grouping && !weekendKeys.has(c.grouping)) {
      weekendKeys.add(c.grouping);
      const dateRange = item.formattedDate
        ? item.formattedDateYear
          ? `${item.formattedDate}, ${item.formattedDateYear}`
          : item.formattedDate
        : null;
      if (dateRange) weekendDateRanges.set(c.grouping, dateRange);
    }

    // Day count from backend classification (computed from tier text or event dates)
    if (c.dayCount !== null && c.dayCount !== undefined && c.dayCount > 0) dayCounts.add(c.dayCount);
  }

  return { filters, weekendKeys, dayCounts, eventCount, weekendDateRanges };
};

/**
 * Resolve the venue name for a city from festival pass events only.
 * Skips parking, camping, and shuttle events which are often at different
 * venues (e.g., "Bill Graham Civic Auditorium" instead of "Golden Gate Park").
 * Falls back to the first candidate if no festival event is found.
 */
export const resolveVenueForCity = (
  gridItems: readonly EventListGridItem[],
  displayCity: string | null,
  classifications: Readonly<Record<string, GaPassClassification>> | undefined,
): string | null => {
  const candidates = displayCity
    ? gridItems.filter((item) => item.venueCity?.toLowerCase() === displayCity.toLowerCase())
    : gridItems;
  const festivalEvent = candidates.find((item) => {
    const cat = classifications?.[String(item.eventId)]?.category;
    return cat === GaPassCategory.Ticket || cat === GaPassCategory.None || cat === undefined;
  });
  return (festivalEvent ?? candidates[0])?.venueName ?? null;
};

/**
 * Build the display text for the location selector trigger.
 * Priority: user-selected label → SEO/paid-search city → first city suggestion.
 * Format: "Venue · City, State, Country" (state included for US locations).
 */
export const buildLocationDisplayText = (
  venueForCity: string | null,
  userSelectedCityLabel: string | null,
  seoCity: CityLocation | null | undefined,
  paidSearchQueryCity: CityLocation | null | undefined,
  citySuggestions: readonly SiteVenueLocation[] | null | undefined,
): string | null => {
  const urlCity = seoCity ?? paidSearchQueryCity;
  const defaultCitySource = urlCity
    ? [urlCity.cityName, urlCity.stateProvinceName, urlCity.countryName]
    : [citySuggestions?.[0]?.city, citySuggestions?.[0]?.state, citySuggestions?.[0]?.country];
  const cityLabel = userSelectedCityLabel ?? defaultCitySource.filter(Boolean).join(", ") ?? null;

  if (!cityLabel) return null;

  if (venueForCity) return `${venueForCity} · ${cityLabel}`;
  return cityLabel;
};
